import { usePermissions } from "@/hooks/use-permissions";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  client,
  DeviceConfigDto,
  ScheduleJobScheduleDto,
  CreateOperationalHoursDto,
} from "@/client";
import { SitesTableContext } from "./-sites-table-context";
import {
  onRegionChange,
  regionBulkFormSchema,
  RegionBulkFormValues,
  toUpdateRegionsBody,
} from "./-sites-region-bulk-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/custom/button";
import { DialogClose, DialogFooter } from "@/components/ui/dialog";
import { ManageTagsComponent } from "@/components/select-input/manage-tags-input";
import { useTags } from "@/hooks/use-tags";
import { useFinalQuery } from "@/hooks/use-finalQuery";
import { useResolveAllSelectedIds } from "@/hooks/use-resolve-all-selected-ids";
import { BulkScheduleDeployment } from "@/components/custom/scheduled-deployments/bulk-schedule-deployment";
import { ServiceName } from "@/components/custom/scheduled-deployments/ServiceNameType";
import { ScheduledConfigDeployment } from "@/components/custom/scheduled-deployments/scheduled-config-deployment";
import { ScheduledModelDeployment } from "@/components/custom/scheduled-deployments/scheduled-model-deployment";
import {
  OperationalHoursEditor,
  OperationalHoursEditorRef,
} from "@/components/custom/operational-hours-editor";
import { useOperationalHoursDefaults } from "@/hooks/use-operational-hours-defaults";
import { Loader } from "@/components/custom/loader";
import { UploadCatalogDialog } from "@/routes/dashboard/data/catalog/-upload-catalog-dialog";
import { FormSelectInputAsyncPaginateQueryItem } from "@/components/custom/form-inputs/select-input/select-input-async-pagination";
import { FormCheckbox } from "@/components/custom/form-inputs/checkbox";

import { showActionErrorToast } from "@/state/error-toast";

export const DeployModelAction: React.FC<{ onClose: () => void }> = ({
  onClose,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const table = SitesTableContext.useTable();

  const { finalQuery } = useFinalQuery({
    context: SitesTableContext,
    idsKey: "siteIds",
    convert: (id: any) => parseInt(id, 10)!,
  });

  const deployModelBulk = useMutation({
    ...client.sites.deployModelBulk.asMutationOptions(),
    onError: (err) => showActionErrorToast(err),
    onSuccess: (_data, variables) => {
      const isScheduled = !!variables.requestBody.schedule;
      toast.success(
        isScheduled ? t("modelScheduledForDeployment") : t("modelDeploying"),
      );
      // Invalidate queries after 5 seconds delay
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: client.devices.listSummaries.queryKey,
        });
      }, 5000);
      // Clear all selections after successful deployment
      table.setAllSelected(false);
      table.resetRowSelection();
      onClose();
    },
  });

  const onSubmit = async (
    modelId: number,
    schedule?: ScheduleJobScheduleDto,
  ) => {
    await deployModelBulk.mutateAsync({
      requestBody: {
        modelId,
        query: finalQuery,
        schedule,
      },
    });
  };

  return <ScheduledModelDeployment onClose={onClose} apiCallback={onSubmit} />;
};

export const ServiceDeploymentAction: React.FC<{ onClose: () => void }> = ({
  onClose,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const table = SitesTableContext.useTable();

  const { finalQuery } = useFinalQuery({
    context: SitesTableContext,
    idsKey: "siteIds",
    convert: (id: any) => parseInt(id, 10)!,
  });

  const deployServiceBulk = useMutation({
    ...client.sites.deployServiceVersionBulk.asMutationOptions(),
    onError: (err) => showActionErrorToast(err),
    onSuccess: () => {
      toast.success(t("componentDeployed"));
      queryClient.invalidateQueries({
        queryKey: client.sites.listSummaries.queryKey,
      });
      table.setAllSelected(false);
      table.resetRowSelection();
      onClose();
    },
  });

  const onSubmit = async (
    serviceName: ServiceName,
    version: string,
    type?: string,
    schedule?: ScheduleJobScheduleDto,
  ) => {
    await deployServiceBulk.mutateAsync({
      requestBody: {
        serviceName,
        version,
        type,
        query: finalQuery,
        schedule,
      },
    });
  };

  return (
    <BulkScheduleDeployment
      apiCallback={onSubmit}
      onClose={onClose}
      resourceType="site"
    />
  );
};

export const TagsActions: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { permissions } = usePermissions();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { finalQuery, count } = useFinalQuery({
    context: SitesTableContext,
    idsKey: "siteIds",
    convert: (id) => parseInt(id, 10)!,
  });

  const updateTagsBulk = useMutation({
    ...client.sites.updateTagsBulk.asMutationOptions(),
    onError: (err) => showActionErrorToast(err),
    onSuccess: () => {
      toast.success(t("updated"));
      onClose();
    },
  });

  // Query invalidation callbacks
  const queryInvalidationCallbacks = [
    () =>
      queryClient.invalidateQueries({
        queryKey: client.sites.listSummaries.queryKey,
      }),
    (hasNewTags?: boolean) => {
      if (hasNewTags) {
        queryClient.invalidateQueries({
          queryKey: client.sites.listTags.queryKey,
        });
      }
    },
  ];
  const {
    tagGroups,
    form,
    formSchema: _formSchema,
    onSubmit,
  } = useTags({
    tagsListQuery: client.sites.listTags
      .assign({
        page: 1,
        limit: 9999,
      })
      .asQueryOptions({}),
    context: SitesTableContext,
    finalQuery,
    updateFunction: updateTagsBulk,
    invalidationCallbacks: queryInvalidationCallbacks,
  });

  return (
    <ManageTagsComponent
      form={form}
      onSubmit={onSubmit!}
      tagGroups={tagGroups}
      canRemoveLocked={permissions.sites.tags.unassignLocked}
      isSubmitting={updateTagsBulk.isPending}
      descriptionKey={`${t("addingEditingTagsDescription")} ${t("for")} ${count} ${count <= 1 ? t("site") : t("sites")}`}
    />
  );
};

// ID-7057 phase 2: bulk region assignment BY ID from the regions tables. Neither select is
// creatable -- regions are created in Settings > Regions -- and the sub-region list cascades off
// the chosen region: picking a different region drops a sub-region chosen under the previous one
// (the pure reducer in -sites-region-bulk-form.ts, so the rule is unit-tested). A region chosen
// with no child is sent with `subRegionId: null` explicitly; the server would clear a stranded
// sub-region anyway, but a request that says what it does reads better in a log.
export const RegionsActions: React.FC<{ onClose: () => void }> = ({
  onClose,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const table = SitesTableContext.useTable();
  const regionLabelById = useRef(new Map<number, string>());
  const subRegionLabelById = useRef(new Map<number, string>());

  const { finalQuery, count } = useFinalQuery({
    context: SitesTableContext,
    idsKey: "siteIds",
    convert: (id) => parseInt(id, 10)!,
  });

  const formSchema = useMemo(() => regionBulkFormSchema(t), [t]);
  const form = useForm<RegionBulkFormValues>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: {},
  });
  const regionId = form.watch("regionId");
  const clear = form.watch("clear") === true;
  // The refine's message sits on `regionId`. Ticking Remove makes the form valid, so Update enables,
  // but react-hook-form re-validates only the field that changed and the stale message would stay
  // under the now-disabled select (review finding).
  useEffect(() => {
    if (clear) form.clearErrors("regionId");
  }, [clear, form]);
  const previousRegionId = useRef(regionId);
  useEffect(() => {
    if (previousRegionId.current === regionId) return;
    // Compare against the PREVIOUS region: getValues() already holds the new one, and comparing new
    // with new never cleared anything -- a sub-region picked under the old region survived into the
    // request and the server answered 400. (Review finding; the live pass never picked a sub-region
    // before switching region.)
    const next = onRegionChange(
      {
        regionId: previousRegionId.current,
        subRegionId: form.getValues("subRegionId"),
      },
      regionId,
    );
    previousRegionId.current = regionId;
    if (next.subRegionId !== form.getValues("subRegionId")) {
      form.setValue("subRegionId", next.subRegionId, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [regionId, form]);

  const updateRegions = useMutation({
    ...client.sites.updateRegions.asMutationOptions(),
    onError: (err) => showActionErrorToast(err),
    onSuccess: () => {
      toast.success(t("regionsUpdated"));
      queryClient.invalidateQueries({
        queryKey: client.sites.listSummaries.queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: client.sites.getDetails.queryKey,
      });
      table.setAllSelected(false);
      table.resetRowSelection();
      onClose();
    },
  });

  const onSubmit = async (values: RegionBulkFormValues) => {
    await updateRegions.mutateAsync({
      requestBody: toUpdateRegionsBody(values, finalQuery),
    });
  };

  const perPage = 10;
  const optionsFrom =
    (labels: React.MutableRefObject<Map<number, string>>) => (data: any) =>
      ((data?.results ?? []) as Array<{ id: number; name: string }>).map(
        (row) => {
          labels.current.set(row.id, row.name);
          return { label: row.name, value: row.id };
        },
      );
  // Typed from the field (number | null | undefined); the mapping is only ever invoked for a
  // defined value, so the nullish branch is for the compiler, not the user.
  const regionMapping = useCallback(
    (v: number | null | undefined) => ({
      value: v as number,
      label: v == null ? "" : (regionLabelById.current.get(v) ?? String(v)),
    }),
    [],
  );
  const subRegionMapping = useCallback(
    (v: number | null | undefined) => ({
      value: v as number,
      label: v == null ? "" : (subRegionLabelById.current.get(v) ?? String(v)),
    }),
    [],
  );
  const hasMore = (data: any, variable: any) =>
    data.results.length >= perPage && data.total > variable.page * perPage;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="mb-4">
          <p className="text-sm text-gray-500 mb-2">
            {t("regionsBulkUpdateDescription", { count })}
          </p>
        </div>

        <FormSelectInputAsyncPaginateQueryItem
          form={form}
          name="regionId"
          label={t("region")}
          isMulti={false}
          isClearable={true}
          isDisabled={clear}
          queryKey={client.sites.getRegions.queryKey}
          queryCreator={client.sites.getRegions.asQueryOptions<any>}
          createOptions={optionsFrom(regionLabelById)}
          hasMore={hasMore}
          requestVariable={(search, page, limit) => ({
            name: search,
            limit: limit ?? perPage,
            page,
          })}
          mapping={regionMapping}
        />
        <FormSelectInputAsyncPaginateQueryItem
          // The option cache is per region -- WITHOUT a remount. Keyed on the region, the select
          // remounted before the cascade effect cleared the value and its Controller captured the
          // old sub-region as the fallback it shows for undefined; keyed on the value as well it
          // remounted on every pick. This form starts empty, so the Controller's fallback is
          // undefined and the cascade's clear shows through.
          cacheUniqs={[regionId]}
          form={form}
          name="subRegionId"
          label={t("subRegion")}
          isMulti={false}
          isClearable={true}
          isDisabled={clear || !regionId}
          placeholder={regionId ? undefined : t("selectARegion")}
          queryKey={client.sites.getSubRegions.queryKey}
          queryCreator={client.sites.getSubRegions.asQueryOptions<any>}
          createOptions={optionsFrom(subRegionLabelById)}
          hasMore={hasMore}
          // No region, no options: the loader treats null as "nothing to offer".
          requestVariable={(search, page, limit) =>
            regionId
              ? { name: search, regionId, limit: limit ?? perPage, page }
              : null
          }
          mapping={subRegionMapping}
        />
        {/* The explicit unassign: a cleared select means "leave unchanged", so it cannot say this. */}
        <FormCheckbox
          form={form}
          name="clear"
          label={t("removeRegionFromSelectedSites")}
        />

        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              {t("cancel")}
            </Button>
          </DialogClose>
          <Button
            type="submit"
            variant="azure"
            disabled={
              updateRegions.isPending ||
              !form.formState.isDirty ||
              !form.formState.isValid
            }
          >
            {updateRegions.isPending ? t("updating") : t("update")}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};

export const SiteGroupAction: React.FC<{ onClose: () => void }> = ({
  onClose,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const table = SitesTableContext.useTable();
  const labelByIdRef = useRef(new Map<number | string, string>());

  const { finalQuery, count } = useFinalQuery({
    context: SitesTableContext,
    idsKey: "siteIds",
    convert: (id) => parseInt(id, 10)!,
  });

  const formSchema = z.object({
    groupId: z.number(),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
  });

  const updateSiteGroupBulk = useMutation({
    ...client.sites.updateSiteGroupBulk.asMutationOptions(),
    onError: (err) => showActionErrorToast(err),
    onSuccess: () => {
      toast.success(t("updated"));
      queryClient.invalidateQueries({
        queryKey: client.sites.listSummaries.queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: client.siteGroups.listSummaries.queryKey,
      });
      table.setAllSelected(false);
      table.resetRowSelection();
      onClose();
    },
  });

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    await updateSiteGroupBulk.mutateAsync({
      requestBody: {
        groupId: data.groupId,
        query: finalQuery,
      },
    });
  };

  const perPage = 10;
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="mb-4">
          <p className="text-sm text-gray-500 mb-2">
            {t("updateSiteGroupDescription", { count })}
          </p>
        </div>

        <FormSelectInputAsyncPaginateQueryItem
          form={form}
          name="groupId"
          label={t("siteGroup")}
          isMulti={false}
          queryKey={client.siteGroups.listSummaries.queryKey}
          queryCreator={client.siteGroups.listSummaries.asQueryOptions<any>}
          createOptions={(data: any) => {
            const results = (data?.results ?? []) as Array<any>;
            return results.map((row) => {
              labelByIdRef.current.set(row.id, row.name);
              return { label: row.name, value: row.id };
            });
          }}
          hasMore={(data: any, variable: any) =>
            data.results.length >= perPage &&
            data.total > variable.page * perPage
          }
          requestVariable={(search, page, limit) => ({
            name: search,
            limit: limit ?? perPage,
            page,
          })}
          mapping={useCallback((v: number | string) => {
            const num = typeof v === "string" ? Number.parseInt(v, 10) : v;
            return {
              value: num,
              label: labelByIdRef.current.get(num) ?? String(num),
            };
          }, [])}
        />

        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              {t("cancel")}
            </Button>
          </DialogClose>
          <Button
            type="submit"
            variant="azure"
            disabled={
              updateSiteGroupBulk.isPending ||
              !form.formState.isDirty ||
              !form.formState.isValid
            }
          >
            {updateSiteGroupBulk.isPending ? t("updating") : t("update")}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};

export const UploadConfigAction: React.FC<{ onClose: () => void }> = ({
  onClose,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const table = SitesTableContext.useTable();

  const { finalQuery, count } = useFinalQuery({
    context: SitesTableContext,
    idsKey: "siteIds",
    convert: (id: any) => parseInt(id, 10)!,
  });

  const deployConfigBulk = useMutation({
    mutationFn: async (data: {
      deviceConfig: Partial<DeviceConfigDto>;
      name?: string;
      description?: string;
      schedule?: ScheduleJobScheduleDto;
    }) => {
      return await client.sites.deployConfigBulk({
        requestBody: {
          query: finalQuery,
          deviceConfig: data.deviceConfig,
          isPartial: true,
          name: data.name,
          description: data.description,
          schedule: data.schedule,
        },
      });
    },
    onError: (err: any) =>
      showActionErrorToast(err, "Failed to deploy configuration"),
    onSuccess: () => {
      toast.success(t("configurationDeployed"));
      queryClient.invalidateQueries({
        queryKey: client.sites.listSummaries.queryKey,
      });
      // Clear all selections after successful deployment
      table.setAllSelected(false);
      table.resetRowSelection();
      onClose();
    },
  });

  const onSubmit = async (data: {
    deviceConfig: Partial<DeviceConfigDto>;
    name?: string;
    description?: string;
    schedule?: ScheduleJobScheduleDto;
  }) => {
    await deployConfigBulk.mutateAsync(data);
  };

  return (
    <div className="w-full mx-auto">
      <ScheduledConfigDeployment
        apiCallback={onSubmit}
        onClose={onClose}
        count={count}
      />
    </div>
  );
};

export const OperationalHoursAction: React.FC<{ onClose: () => void }> = ({
  onClose,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const table = SitesTableContext.useTable();
  const editorRef = useRef<OperationalHoursEditorRef>(null);
  const isAllSelected = table.getAllSelected();

  const selectedIds = table
    .getFilteredSelectedRowModel()
    .rows.map((row) => row.original.id);

  const { finalQuery, count } = useFinalQuery({
    context: SitesTableContext,
    idsKey: "siteIds",
    convert: (id: any) => parseInt(id, 10)!,
  });

  const { defaultValue, isLoading: defaultsLoading } =
    useOperationalHoursDefaults({
      selectedIds,
      isAllSelected,
      mode: "sites",
    });

  const upsertBulk = useMutation({
    ...client.sites.upsertOperationalHoursBulk.asMutationOptions(),
    onError: (err: any) =>
      showActionErrorToast(err, t("operationalHoursSaveFailed")),
    onSuccess: () => {
      toast.success(t("operationalHoursBulkUpdated", { count }));
      queryClient.invalidateQueries({
        queryKey: client.sites.listSummaries.queryKey,
      });
      table.setAllSelected(false);
      table.resetRowSelection();
      onClose();
    },
  });

  const onSave = async (data: CreateOperationalHoursDto) => {
    await upsertBulk.mutateAsync({
      requestBody: {
        ...data,
        query: finalQuery,
      },
    });
  };

  if (defaultsLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader size="md" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {t("operationalHoursBulkDescription", { count })}
      </p>
      <OperationalHoursEditor
        ref={editorRef}
        value={defaultValue}
        onSave={onSave}
        isEditing={true}
      />
      <DialogFooter>
        <DialogClose asChild>
          <Button variant="outline">{t("cancel")}</Button>
        </DialogClose>
        <Button
          variant="azure"
          disabled={upsertBulk.isPending}
          onClick={() => editorRef.current?.submit()}
        >
          {upsertBulk.isPending ? t("saving") : t("save")}
        </Button>
      </DialogFooter>
    </div>
  );
};

export const UploadCatalogAction: React.FC<{ onClose: () => void }> = ({
  onClose,
}) => {
  const { t } = useTranslation();
  const table = SitesTableContext.useTable();
  const isAllSelected = table.getAllSelected();

  const { finalQuery } = useFinalQuery({
    context: SitesTableContext,
    idsKey: "siteIds",
    convert: (id: any) => parseInt(id, 10)!,
  });

  const {
    ids: siteIds,
    isLoading,
    error,
  } = useResolveAllSelectedIds({
    isAllSelected,
    selectedIds: finalQuery.siteIds ?? [],
    finalQuery,
    listSummaries: (params) => client.sites.listSummaries(params),
  });

  // Require at least one site to be selected (including when "select all" resolves to none)
  if (siteIds !== null && siteIds.length === 0) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {t("Please select at least one site to upload a catalog.")}
        </p>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("close")}</Button>
          </DialogClose>
        </DialogFooter>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-destructive">{error}</p>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("close")}</Button>
          </DialogClose>
        </DialogFooter>
      </div>
    );
  }

  if (isLoading || siteIds === null) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader size="md" />
      </div>
    );
  }

  return <UploadCatalogDialog onClose={onClose} initialSiteIds={siteIds} />;
};
